import React, { useState } from "react";
import Page from "../../organisms/Page";
import { BreadCrumbItem } from "../../organisms/HeaderSection";
import { Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
import DownloadIcon from "@mui/icons-material/Download";
import styled from "styled-components";
import {
  IngresoPorMascota,
  useDescargarIngresosPorMascota,
  useObtenerIngresosPorMascota,
} from "../../../services/analisisFinanzas";
import LoadingScreen from "../../organisms/LoadingScreen";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { obtenerStringDeFecha } from "../../../utils/dateUtils";
import { downloadBlob } from "../../../utils/downloadUtils";
import IngresosPorMascotaTable from "./IngresosPorMascotaTable";

const breadCrumbs: BreadCrumbItem[] = [
  { path: "/", label: "Inicio" },
  { path: "/analisis", label: "Análisis" },
  { path: "/analisis/finanzas", label: "Finanzas" },
  {
    path: "/analisis/finanzas/ingresos-por-mascota",
    label: "Ingresos por mascota",
    isActive: true,
  },
];

function IngresosPorMascota() {
  const [fechaInicio, setFechaInicio] = useState<string>(() => {
    const fecha = new Date();
    fecha.setDate(1);
    fecha.setMonth(fecha.getMonth() - 1);
    return obtenerStringDeFecha(fecha);
  });

  const [fechaFin, setFechaFin] = useState<string>(() => {
    return obtenerStringDeFecha(new Date());
  });

  const [itemsPorPagina, setItemsPorPagina] = useState(10);

  const { data, isLoading, error } = useObtenerIngresosPorMascota(
    fechaInicio,
    fechaFin
  );
  const descargaIngresosMutacion = useDescargarIngresosPorMascota();

  const ingresosPorMascota: IngresoPorMascota[] = data || [];

  const descargarExcel = async () => {
    const respuesta = await descargaIngresosMutacion.mutateAsync({
      fechaInicio,
      fechaFin,
    });
    const nombreArchivo = respuesta.nombre
      ? `${respuesta.nombre}${
          respuesta.extension ? `.${respuesta.extension}` : ""
        }`
      : `ingresos_por_mascota_${fechaInicio}_a_${fechaFin}.xlsx`;
    downloadBlob(respuesta.contenido, nombreArchivo);
  };

  if (error) {
    return <ServerErrorModal />;
  }

  return (
    <Page
      title="Ingresos por mascota"
      description="Análisis de ingresos agrupados por mascota."
      breadCrumbs={breadCrumbs}
    >
      <LoadingScreen isLoading={isLoading} />
      <Row className="justify-content-center">
        <Col xs={10} md={8} lg={8} xxl={8}>
          <StyledCard>
            <Card.Body>
              <Row className="mb-3">
                <Col xs={12} md={3}>
                  <Form.Group controlId="fechaInicio">
                    <Form.Label>Fecha inicio</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaInicio}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFechaInicio(e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group controlId="fechaFin">
                    <Form.Label>Fecha fin</Form.Label>
                    <Form.Control
                      type="date"
                      value={fechaFin}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFechaFin(e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={3}>
                  <Form.Group controlId="itemsPorPagina">
                    <Form.Label>Items por página</Form.Label>
                    <Form.Select
                      value={itemsPorPagina}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setItemsPorPagina(parseInt(e.target.value))
                      }
                    >
                      {[5, 10, 15].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={3} className="d-flex align-items-end">
                  <Button
                    variant="primary"
                    onClick={descargarExcel}
                    disabled={descargaIngresosMutacion.isLoading}
                  >
                    {descargaIngresosMutacion.isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Descargando...
                      </>
                    ) : (
                      <>
                        <DownloadIcon sx={{ fontSize: 20, marginRight: "8px" }} />
                        Descargar
                      </>
                    )}
                  </Button>
                </Col>
              </Row>

              <IngresosPorMascotaTable
                ingresosPorMascota={ingresosPorMascota}
                isLoading={isLoading}
                itemsPorPagina={itemsPorPagina}
              />
            </Card.Body>
          </StyledCard>
        </Col>
      </Row>
    </Page>
  );
}

const StyledCard = styled(Card)`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export default IngresosPorMascota;
